import React, { useState, useEffect } from "react";
import { ClipboardList, Database, X, Loader2, Plus } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchVoucherData,
  addVoucherCodes,
  selectVoucherData,
  selectVoucherStatus,
  selectAddCodesStatus,
} from "../../../RTK/voucherSlice";
import { toast } from "react-toastify";

export const StockOptionsModal = ({ isOpen, onClose, onSelect, productName }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4" dir="rtl">
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl w-full max-w-md">
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between border-b border-gray-700 pb-6">
            <div>
              <h3 className="text-xl font-semibold text-white">إدارة المخزون</h3>
              {productName && (
                <p className="text-sm text-gray-400 mt-1">{productName}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-700 rounded-xl transition-colors"
            >
              <X className="w-6 h-6 text-gray-400" />
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <button
              onClick={() => onSelect("details")}
              className="flex items-center justify-center gap-3 px-6 py-4 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-colors"
            >
              <ClipboardList className="w-5 h-5 text-yellow-500" />
              <span>التفاصيل</span>
            </button>

            <button
              onClick={() => onSelect("manage")}
              className="flex items-center justify-center gap-3 px-6 py-4 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-colors"
            >
              <Database className="w-5 h-5 text-yellow-500" />
              <span>إدارة المخزون</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AddVoucherCodesModal = ({ isOpen, onClose, serviceId }) => {
  const dispatch = useDispatch();
  const addStatus = useSelector(selectAddCodesStatus);
  const [codesInput, setCodesInput] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const codes = codesInput
      .split('\n')
      .map(code => code.trim())
      .filter(code => code !== '');

    if (codes.length === 0) {
      toast.error('الرجاء إدخال كود واحد على الأقل');
      return;
    }

    try {
      const result = await dispatch(addVoucherCodes({ serviceId, codes })).unwrap();
      toast.success(`تم إضافة ${result.total_created} كود بنجاح`);
      onClose();
    } catch (error) {
      toast.error(error?.message || 'فشل في إضافة الأكواد');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[60] flex items-center justify-center p-4" dir="rtl">
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl w-full max-w-md">
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between border-b border-gray-700 pb-6">
            <div>
              <h3 className="text-xl font-semibold text-white">إضافة أكواد جديدة</h3>
              <p className="text-sm text-gray-400 mt-1">أدخل كل كود في سطر جديد</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-700 rounded-xl transition-colors"
            >
              <X className="w-6 h-6 text-gray-400" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <textarea
              value={codesInput}
              onChange={(e) => setCodesInput(e.target.value)}
              placeholder={`VOUCHER123\nVOUCHER124\nVOUCHER125`}
              className="w-full h-48 bg-gray-800 border border-gray-700 rounded-xl p-3 text-white resize-none"
              required
            />

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-xl bg-gray-700 text-white hover:bg-gray-600 transition-colors"
              >
                إلغاء
              </button>
              <button
                type="submit"
                disabled={addStatus === 'loading'}
                className="px-4 py-2 rounded-xl bg-yellow-500 text-black font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {addStatus === 'loading' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    جاري الإضافة...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    إضافة الأكواد
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export const VoucherManagementModal = ({ isOpen, onClose, serviceId }) => {
  const dispatch = useDispatch();
  const voucherData = useSelector(selectVoucherData);
  const status = useSelector(selectVoucherStatus);
  const [showAddCodes, setShowAddCodes] = useState(false);

  useEffect(() => {
    if (isOpen && serviceId) {
      dispatch(fetchVoucherData(serviceId));
    }
  }, [isOpen, serviceId, dispatch]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4" dir="rtl">
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl w-full max-w-2xl">
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between border-b border-gray-700 pb-6">
            <div>
              <h3 className="text-xl font-semibold text-white">
                {voucherData.serviceName}
              </h3>
              <p className="text-gray-400 mt-1">إدارة المخزون</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-700 rounded-xl transition-colors"
            >
              <X className="w-6 h-6 text-gray-400" />
            </button>
          </div>

          {status === "loading" ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
            </div>
          ) : status === "failed" ? (
            <div className="text-center py-12 text-red-500">
              {voucherData.error || "حدث خطأ أثناء تحميل البيانات"}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-800/50 p-4 rounded-xl border border-gray-700">
                  <p className="text-sm text-gray-400">إجمالي الأكواد</p>
                  <p className="text-2xl font-bold text-white mt-1">
                    {voucherData.totalCodes}
                  </p>
                </div>
                <div className="bg-gray-800/50 p-4 rounded-xl border border-gray-700">
                  <p className="text-sm text-gray-400">الأكواد المتاحة</p>
                  <p className="text-2xl font-bold text-white mt-1">
                    {voucherData.availableCodes}
                  </p>
                </div>
              </div>

              {/* Voucher Codes List */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold text-white">الأكواد</h4>
                  <button
                    onClick={() => setShowAddCodes(true)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-yellow-500 text-black rounded-lg hover:bg-yellow-400 transition-colors text-sm font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    إضافة كود جديد
                  </button>
                </div>

                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {voucherData.voucherCodes.map((voucher) => (
                    <div
                      key={voucher.id}
                      className="flex items-center justify-between bg-gray-800/50 p-4 rounded-xl border border-gray-700"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            voucher.status ? "bg-green-500" : "bg-red-500"
                          }`}
                        />
                        <span className="font-mono text-white">
                          {voucher.code}
                        </span>
                      </div>
                      <span
                        className={`text-sm ${
                          voucher.status ? "text-green-500" : "text-red-500"
                        }`}
                      >
                        {voucher.status ? "متاح" : "مستخدم"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    <AddVoucherCodesModal
        isOpen={showAddCodes}
        onClose={() => setShowAddCodes(false)}
        serviceId={serviceId}
      />
    </div>
  );
};